import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { BaseAutoCompleteComponent } from '../../../base-auto-complete.component';
import { Stop } from 'src/app/modules/route-planner/interfaces/route-planning/gtfs/stop.interface';
import { FavoritesService } from 'src/app/modules/route-planner/services/favorites/favorites.service';
import { RoutePlanningService } from 'src/app/modules/route-planner/services/route-planning/route-planning.service';
import { ResizeService } from 'src/app/@shared/services/resize/resize.service';
import { Overlay } from '@angular/cdk/overlay';
import { DATA_INJECTION_TOKEN } from 'src/app/@shared/services/overlay/overlay.service';
import { tap, debounceTime, distinctUntilChanged, switchMap, of, Subject, takeUntil } from 'rxjs';
import { SlideUpCardComponent } from 'src/app/@shared/components/design/slide-up-card/slide-up-card.component';
import { ModalService } from 'src/app/@shared/services/modal/modal.service';
import { AutoCompleteService } from 'src/app/modules/route-planner/services/auto-complete/auto-complete.service';
import { AddressAutoCompleteMapComponent } from '../../source-destination-form/address-auto-complete/address-auto-complete-map/address-auto-complete-map.component';
import { RequestPoiComponent } from '../../source-destination-form/request-poi/request-poi.component';
import { FavoriteStations } from 'src/app/modules/route-planner/interfaces/favorites/favorite-stations.interface';
import { eFavoriteType } from 'src/app/modules/route-planner/enums/favorite/favorite-type.enum';
import { LanguageService } from 'src/app/@shared/services/language/language.service';
import { Direction } from '@angular/cdk/bidi';
import { CANT_FIND_USER_LOCATION_STATION, FOR, CLEAR, NO_STATION_FOUND, SEARCH_STATION, STATION_SEARCHING } from 'src/app/modules/route-planner/constants/multi-language.constants';
import { ScheduleService } from 'src/app/modules/route-planner/services/schedule/schedule.service';
import { UtilsService } from 'src/app/@shared/services/utils/utils.service';
import { Coordinate } from 'src/app/modules/route-planner/interfaces/location/coordinate.interface';
import { UserLocationService } from 'src/app/@shared/services/user-location/user-location.service';
import { StationsAutoCompleteMapComponent } from './stations-auto-complete-map/stations-auto-complete-map.component';
import {
  createSlideUpListboxId,
  SlideUpComboboxListKeyboard,
} from '../../../shared/slide-up-combobox-list-keyboard.controller';

export interface StopAutoCompleteData {
  station: Stop;
  title: string;
  initialValue?: string;
  hideFeed?: boolean;
  isInnerCard?: boolean;
  saveResultToStorage?: boolean;
}

@Component({
    selector: 'app-stations-auto-complete',
    templateUrl: './stations-auto-complete.component.html',
    styleUrls: ['./stations-auto-complete.component.scss'],
    standalone: false
})
export class StationsAutoCompleteComponent
  extends BaseAutoCompleteComponent<Stop>
  implements OnInit {
  @ViewChild('card') override card: SlideUpCardComponent;

  private destroy$ = new Subject<void>();


  selectedTabIndex: number = 0;

  favoriteStations: FavoriteStations;

  locationPermissionGranted: boolean = !!this.userLocationService.hasPermissions;
  iconName: string
  searchStationText: string
  title: string
  noStationFoundText: string
  clearText: string
  direction: Direction

  isCurrentLocationLoading: boolean = false

  /**
   * a11y: WAI-ARIA combobox-autocomplete-list — keyboard via SlideUpComboboxListKeyboard.
   */
  readonly listboxId = createSlideUpListboxId('mot-stations-listbox');
  currentResults: Stop[] = [];
  readonly comboboxKeyboard: SlideUpComboboxListKeyboard;

  @ViewChild('input', { read: ElementRef })
  private searchInputHost?: ElementRef<HTMLElement>;

  constructor(
    resizeService: ResizeService,
    overlay: Overlay,
    favoritesService: FavoritesService,

    public override routePlanningService: RoutePlanningService,
    @Inject(DATA_INJECTION_TOKEN)
    public readonly data: StopAutoCompleteData,
    private readonly autoCompleteService: AutoCompleteService,
    private readonly modalService: ModalService,
    private readonly languageService: LanguageService,
    private readonly scheduleService: ScheduleService,
    private readonly userLocationService: UserLocationService
  ) {
    super(favoritesService, routePlanningService, resizeService, overlay);
    this.comboboxKeyboard = new SlideUpComboboxListKeyboard(
      this.listboxId,
      () => this.currentResults,
      () => this.isListboxExpanded,
      () => this.searchInputHost?.nativeElement,
      (index) =>
        this.chooseValue(
          this.currentResults[index],
          !!this.data.saveResultToStorage
        )
    );
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.initializeStationsFormControl();

    this.favoriteStations = this.favoritesService.favorites?.stations;

    this.favoritesService.onFavoritesChange$.subscribe((favorites) => {
      this.favoriteStations = favorites?.stations;
    });

    this.initializeMultiLanguageTexts()
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy()
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeMultiLanguageTexts() {
    this.languageService.selectedLanguage$.subscribe(language => {
      this.direction = language.direction
      this.searchStationText = SEARCH_STATION[language.language]
      this.title = STATION_SEARCHING[language.language]
      this.noStationFoundText = `${NO_STATION_FOUND[language.language]} ${FOR[language.language]}`
      this.clearText = CLEAR[language.language]
      this.iconName = language.direction === "rtl" ? "chevron-right" : "chevron-left"
    })
  }

  private initializeStationsFormControl() {
    if (!!this.data?.station) {
      this.formControl.setValue(this.data.station.name[this.languageService.getSelectedLanguage().symbol] || this.data.station.name.he || '', {
        emitEvent: false,
      });
    }

    this.formControl.valueChanges
      .pipe(
        tap(() => {
          this.emptyResults$.next(false);
          this.comboboxKeyboard.resetHighlight();
        }),
        debounceTime(this.debounceTime),
        distinctUntilChanged(),
        switchMap((val) => this.autoCompleteService.searchStations(val)),
        takeUntil(this.destroy$)
      )
      .subscribe((results) => {
        this.results$ = of(results);
        this.currentResults = (results || []).map((r) => r.value);
        this.emptyResults$.next(results.length === 0);
        this.comboboxKeyboard.resetHighlight();
      });
  }

  getOptionId(index: number): string {
    return this.comboboxKeyboard.getOptionId(index);
  }

  get activeOptionId(): string | null {
    return this.comboboxKeyboard.activeOptionId;
  }

  get activeOptionIndex(): number {
    return this.comboboxKeyboard.activeOptionIndex;
  }

  /** True when the listbox is visible with at least one option (drives aria-expanded). */
  get isListboxExpanded(): boolean {
    return (
      !!this.formControl.value &&
      this.formControl.value.length >= this.autoCompleteMinChar &&
      this.currentResults.length > 0
    );
  }

  /**
   * Delegates to shared slide-up combobox keyboard controller.
   */
  onComboboxKeydown(event: KeyboardEvent): void {
    this.comboboxKeyboard.onComboboxKeydown(event);
  }

  onListboxKeydown(event: KeyboardEvent): void {
    this.comboboxKeyboard.onListboxKeydown(event);
  }

  onListboxContainerFocus(event: FocusEvent): void {
    this.comboboxKeyboard.onListboxContainerFocus(event);
  }

  onOptionFocus(index: number): void {
    this.comboboxKeyboard.onOptionFocus(index);
  }

  protected override insertChosenValueToStorage(stop: Stop) {
    let favoriteStations = this.favoritesService.favorites?.stations;

    if (!favoriteStations?.lastSearches) {
      favoriteStations = {
        ...favoriteStations,
        type: eFavoriteType.STATIONS,
        lastSearches: [],
      };
    }

    if (
      favoriteStations.lastSearches.length >= this.FEED_LAST_SEARCHES_AMOUNT
    ) {
      favoriteStations.lastSearches.pop();
    }

    if (
      !favoriteStations.lastSearches.find(
        (search) => search.stopcode === stop.stopcode
      )
    ) {
      favoriteStations.lastSearches.unshift({
        ...stop,
        type: eFavoriteType.LAST_SEARCH,
      });
    }

    this.favoritesService.updateFavorite(favoriteStations);
  }

  handleCurrentLocationClick() {
    if(this.userLocationService.userLocation) {
      this.isCurrentLocationLoading = true
      this.scheduleService.getClosestStop(this.userLocationService.userLocation).subscribe(res => {
        this.isCurrentLocationLoading = false
        if (!!res.data) {
          this.insertChosenValueToStorage(res.data);
        }
        this.closeCard(res.data)
      });
    } else {
      this.modalService.openCantFindUserLocationModal(CANT_FIND_USER_LOCATION_STATION)
    }
  }

  handleMapClick(initialMapValue?: string) {
    if (!!this.isDesktop) {
      this.routePlanningService._mapOverlaySub.next({
        mapComponent: StationsAutoCompleteMapComponent,
        data: {
          ...this.data,
          initialValue: initialMapValue,
          closeCallbackFn: (value) => {
            if (!!value) {
              this.insertChosenValueToStorage(value);
              this.closeCard(value);
            }
            this.routePlanningService._mapOverlaySub.next(null);
          },
        },
      });
    }
    else {
      this.openComponentOverlay(
        this.direction,
        StationsAutoCompleteMapComponent,
        { ...this.data, initialValue: initialMapValue },
        this.card.cardElement,
        (value) => {
          if (!!this.modalService.popUpRef) {
            this.modalService.closePopUp();
          }

          if (!!value) {
            this.insertChosenValueToStorage(value);
            this.closeCard(value);
          }
        }
      );
    }
  }

  openRequestPoiModal(value: any): void {
    this.modalService.openPopUp(RequestPoiComponent, {
      data: {
        poiName: value,
      },
    });
  }

  override handleOverlayEscape(): void {
    this.routePlanningService._mapOverlaySub.next(null);
    this.closeCard();
  }
}
